import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Shop } from '@app/services/shops/shop.model';
import {
  CountryCode,
  getCountryCodeByCountryName,
  getCountryDisplayNameByCountryCode,
} from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Observable, Subscription, Subject } from 'rxjs';

import { modifiableValue, VerificationField, verificationFields } from './model';
import { ShopService } from '@app/services/shops/shop.service';
import { verificationFieldsData, weatherData } from '../../../services/settings/settings-dto';
import { FieldType, SettingsService } from '../../../services/settings/settings.service';
import { Router } from '@angular/router';
import { ToastService } from '../../../storybook/services/toast/toast.service';
import { takeUntil } from 'rxjs/operators';
@Component({
  selector: 'verifications',
  templateUrl: './verifications.component.html',
  styleUrls: ['./verifications.component.scss', '../settings.scss'],
  standalone: false,
})
export class VerificationsComponent implements OnInit, OnDestroy {
  readonly FieldType = FieldType;
  formGroup: UntypedFormGroup;
  initialForm;
  metadata: verificationFieldsData;
  fieldsList: VerificationField[];
  loadFields$: Observable<any[]>;
  loadFieldSubscription$: Subscription;
  saveComplete$: Observable<any>;
  saveCompleteSubscription$: Subscription;
  isLoading = true;
  notification = { duration: 'short', msg: '', type: 'info', state: 'out' };
  shopCountryCode: CountryCode | null;
  shop: Shop;

  destroyed$ = new Subject();

  constructor(
    private settingsSrv: SettingsService,
    private formBuilder: UntypedFormBuilder,
    private elementRef: ElementRef,
    private changeDetector: ChangeDetectorRef,
    private shopService: ShopService,
    private toastService: ToastService,
    private router: Router,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    /*const shop = this.apiService.shop.subscribe((shop) => {
        this.shopCountry = this.getShopCountry(shop);
        this.loadFields();
      }); */

    this.shopService.shop$.pipe(takeUntil(this.destroyed$)).subscribe((shop) => {
      if (!shop) {
        return;
      }

      this.shop = { id: shop.id, isCheckoutOnly: shop.isCheckoutOnly } as Shop;
      this.shopCountryCode = shop.address?.country
        ? getCountryCodeByCountryName(shop.address?.country)
        : shop.address?.countryCode
          ? getCountryCodeByCountryName(shop.address?.countryCode)
          : null;
      this.loadFields();
    });
  }

  async loadFields() {
    this.loadFields$ = this.settingsSrv.getVreificationSettings();
    this.loadFieldSubscription$ = this.loadFields$.subscribe(
      (res) => {
        this.metadata = new verificationFieldsData(res);
        this.initForm(this.metadata);
        this.isLoading = false;
      },
      () => {
        this.router.navigate(['settings']);
      },
    );
  }

  initForm(metadata: verificationFieldsData) {
    const formFields = {};
    const displayedFields = verificationFields
      .filter((f) => !f.displayCondition || f.displayCondition(this.shopCountryCode, this.shop))
      .map((f) => {
        if (f.additionalInfo) {
          f.additionalInfo.data = f.additionalInfo.data.filter(
            (af) => !af.displayCondition || af.displayCondition(this.shopCountryCode, this.shop),
          );
          if (!f.additionalInfo.data || !f.additionalInfo.data.length) f.additionalInfo = undefined;
        }
        return f;
      });

    displayedFields
      .filter((f) => f.dataName)
      .map((f) => {
        const val = metadata[f.dataName];
        formFields[f.dataName] = [val];
      });

    displayedFields
      .filter((f) => f.additionalInfo)
      .forEach((f) => {
        return f.additionalInfo.data.map((fi) => {
          let val = this.getObjValueByPath(metadata, fi.dataName);
          val = typeof val !== 'undefined' ? val : typeof fi.default !== 'undefined' ? fi.default : '';
          const validators = fi.validators ?? [];
          formFields[fi.dataName] = [val, validators];
        });
      });

    this.fieldsList = displayedFields;
    this.formGroup = this.formBuilder.group(formFields);
    this.initialForm = this.formGroup.value;
  }
  onSubmit(): void {
    if (!this.formGroup.valid || !this.formGroup.dirty) return;

    const values = this.formGroup.value;
    const modifiedValues: modifiableValue[] = [];
    this.fieldsList.map((f) => {
      const control = f.dataName ? this.formGroup.get(f.dataName) : null;
      if (f.dataName == 'isActiveWeather') {
        this.adjustmentWeatherValues(values, f, modifiedValues);
      } else if (f.dataName == 'verifyInventory') {
        modifiedValues.push({ path: f.dataName, value: values[f.dataName] ? f.stringValue : values[f.dataName] });
      } else if (control?.dirty) {
        modifiedValues.push({ path: f.dataName, value: values[f.dataName] });
      }
      if (f.additionalInfo) {
        f.additionalInfo.data.map((addiotionalField) => {
          const control = addiotionalField.dataName ? this.formGroup.get(addiotionalField.dataName) : null;
          if (addiotionalField.dataName == 'inventoryPollingTime') {
            modifiedValues.push({
              path: addiotionalField.dataName,
              value:
                values[f.dataName] && values[addiotionalField.dataName]
                  ? values[addiotionalField.dataName] === true
                    ? addiotionalField.stringValue
                    : values[addiotionalField.dataName]
                  : undefined,
            });
          } else if (addiotionalField.dataName.includes('weather')) {
            //do nothing
          } else if (control?.dirty) {
            modifiedValues.push({ path: addiotionalField.dataName, value: values[addiotionalField.dataName] });
          }
        });
      }
    });

    this.saveComplete$ = this.settingsSrv.saveVerifications(modifiedValues);
    this.saveCompleteSubscription$ = this.saveComplete$.subscribe(
      (res) => {
        this.notify('info', 'Saved');
        this.setInitialValues(values);
        this.shopService.refreshShop();
      },
      (err) => {
        this.notify('fail', 'Failed to save settings');
      },
    );
  }

  adjustmentWeatherValues(values, field, modifiedValues) {
    if (!this.validateWeatherFields()) {
      modifiedValues.push({ path: 'weather', value: false });
      return;
    }
    const isActiveMinTemp = values['weather-isActiveMinTemp'];
    const isActiveMaxTemp = values['weather-isActiveMaxTemp'];
    const minTemp = values['weather-minTemp'];
    const maxTemp = values['weather-maxTemp'];
    const processingDays = values['weather-processingDays'];
    const weatherValue: Partial<weatherData> = {};
    if (isActiveMinTemp) weatherValue.minTemp = minTemp;
    if (isActiveMaxTemp) weatherValue.maxTemp = maxTemp;
    weatherValue.units = this.metadata?.weather?.units ?? 'imperial';
    weatherValue.pollingTime = this.metadata?.weather?.pollingTime ?? '6:00';
    weatherValue.processingDays = processingDays ?? 0;
    modifiedValues.push({ path: 'weather', value: weatherValue });
  }

  resetForm() {
    this.formGroup.setValue({ ...this.initialForm });
  }

  resetWeatherFields() {
    const weatherInitialValues = Object.fromEntries(
      Object.entries(this.initialForm).filter(([key, val]) => key.indexOf('weather-') > -1),
    );
    this.formGroup.patchValue(weatherInitialValues);
  }

  clearField(fieldName) {
    this.formGroup.patchValue({ [fieldName]: '' });
  }

  setInitialValues(values) {
    this.initialForm = values;
    // this.metadata = new verificationFieldsData(metadata);
    this.formGroup.markAsPristine();
  }

  validateWeatherFields() {
    const values = this.formGroup.value;
    if (!values['isActiveWeather'] || (!values['weather-isActiveMinTemp'] && !values['weather-isActiveMaxTemp'])) {
      this.setControlValue('isActiveWeather', false);
      return false;
    }
    if (
      (values['weather-isActiveMinTemp'] && !values['weather-minTemp']) ||
      (values['weather-isActiveMaxTemp'] && !values['weather-maxTemp'])
    ) {
      return false;
    }
    return true;
  }

  /* UI events */

  weatherToggled(e) {
    if (!e) this.resetWeatherFields();
  }

  tempToggled(e, fieldName) {
    if (e) {
      const validators: ValidatorFn[] = [Validators.required];
      validators.concat(verificationFields[fieldName].validators);
      this.formGroup.controls[fieldName].setValidators(validators);
    } else if (!e) {
      this.formGroup.controls[fieldName].clearValidators();
      this.clearField(fieldName);
    }
  }

  onTempChanged(fieldName: string, newVal) {
    if (fieldName.includes('max') && newVal && newVal.length > 0) {
      if (this.formGroup.controls['weather-isActiveMaxTemp'].value == false)
        this.setControlValue('weather-isActiveMaxTemp', true);
      //this.formGroup.get('_isActiveMaxTemp').setValue(true);
    } else if (fieldName.includes('min') && newVal && newVal.length > 0) {
      if (this.formGroup.controls['weather-isActiveMinTemp'].value == false)
        this.setControlValue('weather-isActiveMinTemp', true);
    }
  }

  onTempBlur(fieldName: string, e) {
    if (!e.target.value || e.target.value === '') {
      if (fieldName.includes('min') && this.formGroup.controls['weather-isActiveMinTemp'].value == true)
        this.setControlValue('weather-isActiveMinTemp', false);
      if (fieldName.includes('max') && this.formGroup.controls['weather-isActiveMaxTemp'].value == true)
        this.setControlValue('weather-isActiveMaxTemp', false);
    }
  }

  /* UI Getters */

  get fieldsAreaClasses() {
    return this.footerState.includes('apear') ? ['footerBottomPadding'] : [''];
  }

  get isDisplayedWeather() {
    return this.formGroup?.value['isActiveWeather'];
  }

  get isCheckedInventory() {
    return this.formGroup?.value['verifyInventory'];
  }

  get changesCount() {
    if (!this.formGroup || !this.initialForm) return;
    return Object.entries(this.formGroup?.controls).filter((value) => {
      if (value[1] instanceof UntypedFormControl) return value[1].value != this.initialForm[value[0]];
      if (value[1] instanceof UntypedFormGroup) return value[1].value[value[0]] != this.initialForm[value[0]][value[0]];
    }).length;
  }
  get changedStatusTxt() {
    if (!this.isChanged) return '';
    return ` You've changed ${this.changesCount} setting `;
  }
  get isChanged() {
    return this.formGroup.dirty && this.changesCount > 0;
  }
  get footerState() {
    return !this.formGroup.dirty ? ['initial'] : this.isChanged ? ['apear'] : ['disapear'];
  }

  get isEnabledSave() {
    return this.formGroup.valid;
  }

  isInvalidField(fieldName: string) {
    return this.formGroup.dirty && !this.formGroup.controls[fieldName].valid;
  }
  isDisabledFeature(fieldName: string) {
    switch (fieldName) {
      case 'flagMediumFraudRisk':
        return this.formGroup.value.verifyFraudRisk === false;
      case 'inventoryPollingTime':
        return !this.formGroup.value.verifyInventory;
      case 'flagWeightInAssign':
        return !this.formGroup.value.supportsMeasuresValidation;
      case 'flagPackageInAssign':
        return !this.formGroup.value.supportsPackageValidation;
      default:
        return false;
    }
  }
  getInvalidTemperatureTxt(minTempField, maxTempField) {
    if (this.formGroup.controls[minTempField.dataName].hasError('min')) return 'Brr that temperature is too low';
    else if (this.formGroup.controls[minTempField.dataName].hasError('max')) return 'Ahh that temperature is too high';
    else if (this.formGroup.controls[maxTempField.dataName].hasError('min')) return 'Brr that temperature is too low';
    else if (this.formGroup.controls[maxTempField.dataName].hasError('max')) return 'Ahh that temperature is too high';
    /*else if(this.getFieldVal(minTempField.dataName) > maxTempField.dataName)
      return 'Min temperature must be lower than max';*/
    return;
  }

  /* Private methods */

  getShopCountry(shop: Shop): string {
    const shopCountry = shop.address?.countryCode ?? shop.address?.country;
    return getCountryDisplayNameByCountryCode(shopCountry ?? CountryCode.IL);
  }

  setControlValue(controlName: string, val: any) {
    this.formGroup.patchValue({ [controlName]: val });
  }

  getFieldVal(controlName) {
    return this.formGroup.controls[controlName].value;
  }

  getObjValueByPath(obj: object, path: string) {
    const val = path.split('-').reduce((val, currPath) => (val ? val[currPath] : ''), obj);

    return val;
  }

  setObjValueByPath(obj: object, path: string, value: any) {
    const properties: string[] = path.split('.');

    properties.reduce((obj, prop, idx) => {
      if (idx == properties.length - 1 && obj.hasOwnProperty(prop)) obj[prop] = value;
      return obj[prop];
    }, obj);
  }

  notify(type: string, msg: string) {
    this.notification.duration = 'short';
    this.notification.msg = msg;
    this.notification.type = type;
    this.notification.state = 'in';

    setTimeout(() => (this.notification.state = 'out'), 5000);
  }

  getObjSubsetEntries(obj, filter) {
    Object.fromEntries(Object.entries(obj).filter(([key, val]) => filter(key)));
  }
}
